import React, { useEffect, useRef, useState } from "react";
import {
  Control as RHFControl,
  Controller,
  Field,
  FieldErrors,
  useWatch,
} from "react-hook-form";
import {
  StyleSheet,
  TextInput as RNTextInput,
  FlatList,
  ActivityIndicator,
} from "react-native";
import Colors from "../../constants/Colors";
import { View, Text } from "../Themed";
import { Feather } from "@expo/vector-icons";
import Pressable from "../Pressable";
import useGetProperty from "../../hooks/useGetByBlossor";
import useGetByBlossor from "../../hooks/useGetByBlossor";

type Control = RHFControl<any, any>;

interface InputSearchProps {
  control: Control;
  label: string;
  name: string;
  errors: FieldErrors;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  options: { label: string; value: any }[];
  isLoading?: boolean;
  onClear?: () => void;
  onSelected?: (item: any) => void;
  blossorId?: string;
}

const InputSearch: React.FC<InputSearchProps> = ({
  control,
  label,
  name,
  errors,
  placeholder = "",
  required = false,
  multiline = false,
  options,
  isLoading = false,
  onClear,
  onSelected,
  blossorId,
}) => {
  const inputRef = useRef<RNTextInput>(null);

  const [formState, setFormState] = useState<
    "default" | "active" | "error" | "selected"
  >("default");

  const { data: property } = useGetByBlossor(blossorId);

  return (
    <View style={{ width: "100%", zIndex: 1, marginTop: 10 }}>
      <Controller
        control={control}
        rules={{
          required: required ? "This field is required" : false,
        }}
        render={({ field: { onChange, onBlur, value } }) => {
          React.useEffect(() => {
            if (property) {
              const propertyLabel = property.address;
              const propertyText =
                propertyLabel.street +
                " " +
                propertyLabel.city +
                " " +
                propertyLabel.state +
                " " +
                propertyLabel.zip;
              const formattedValue = {
                label: propertyText,
                value: {
                  fieldValues: {
                    address: propertyLabel.street,
                    city: propertyLabel.city,
                    ln: propertyLabel.mlsNum,
                    postalCode: propertyLabel.zip,
                    state: propertyLabel.state,
                  },
                  suggestType: "fullAddress",
                  text: propertyText,
                },
              };

              onChange(formattedValue);
            }
          }, [property]);

          return (
            <View style={styles.body}>
              <View style={styles.labelRow}>
                <Text style={styles.labelText}>{label}</Text>
                {required && <Text style={styles.errorStar}>*</Text>}
              </View>
              <Pressable
                hitSlop={styles.hit}
                onPress={() => {
                  inputRef.current?.focus();
                }}
                style={[
                  styles.input,
                  errors[name] !== undefined
                    ? styles.inputError
                    : formState === "active"
                    ? styles.inputActive
                    : null,
                ]}
              >
                <RNTextInput
                  ref={inputRef}
                  onBlur={() => {
                    onBlur();
                    if (formState === "selected") {
                      setFormState("default");
                    }
                  }}
                  multiline={multiline}
                  onFocus={() => {
                    setFormState("active");
                  }}
                  onChangeText={onChange}
                  value={value?.label || value}
                  style={[styles.InputSearch, multiline && { height: 100 }]}
                  placeholder={placeholder}
                  placeholderTextColor={Colors.nuetral_grey_600}
                />
              </Pressable>
              {formState === "active" &&
              (options?.length > 0 || value?.length > 0) ? (
                <View
                  style={[
                    styles.inputDropdown,
                    errors[name] !== undefined
                      ? styles.inputError
                      : formState === "active"
                      ? styles.inputActive
                      : null,
                  ]}
                >
                  <FlatList
                    keyboardShouldPersistTaps="handled"
                    data={
                      options.length > 0
                        ? options
                        : [
                            {
                              label: value,
                              value: value,
                            },
                          ]
                    }
                    contentContainerStyle={styles.content}
                    keyExtractor={(item) => item?.label}
                    renderItem={({ item }) =>
                      isLoading ? (
                        <View style={styles.loading}>
                          <ActivityIndicator size="small" color="black" />
                        </View>
                      ) : (
                        <Pressable
                          onPress={() => {
                            if (onSelected) {
                              onSelected(item);
                              onChange("");
                            } else {
                              onChange(item);
                            }
                            setFormState("selected");
                            inputRef.current?.blur();
                          }}
                          disabled={name === "import" && options.length === 0}
                        >
                          {options.length > 0 ? (
                            <Text numberOfLines={1} style={styles.itemText}>
                              {item?.label}
                            </Text>
                          ) : name !== "import" ? (
                            <View
                              style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingRight: 10,
                              }}
                            >
                              <Text numberOfLines={1} style={styles.itemText}>
                                {`Create tag "${item?.label}"`}
                              </Text>
                              <Feather
                                name="plus-circle"
                                size={18}
                                color={Colors.primary_500}
                              />
                            </View>
                          ) : (
                            <View
                              style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingRight: 10,
                              }}
                            >
                              <Text numberOfLines={1} style={styles.itemText}>
                                No Results
                              </Text>
                            </View>
                          )}
                        </Pressable>
                      )
                    }
                  />
                </View>
              ) : null}
              {value?.length > 0 || value?.value ? (
                <Pressable
                  hitSlop={styles.hit}
                  onPress={() => {
                    onChange("");
                    inputRef.current?.focus();
                    onClear && onClear();
                  }}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: 33,
                  }}
                >
                  <Feather name="x" size={20} color={Colors.nuetral_grey_700} />
                </Pressable>
              ) : null}
            </View>
          );
        }}
        name={name}
      />
      {errors[name] ? (
        <Text style={styles.errorText}>{errors[name]?.message}</Text>
      ) : (
        <Text style={styles.space} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    flex: 1,
  },
  content: {
    paddingVertical: 4,
  },
  itemText: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  loading: {
    padding: 6,
    justifyContent: "center",
    alignItems: "flex-start",
    paddingHorizontal: 16,
  },
  inputDropdown: {
    position: "absolute",
    zIndex: 2,
    width: "100%",
    backgroundColor: Colors.nuetral_grey_100,
    elevation: 10,
    top: 62,
    left: 0,
    right: 0,
    maxHeight: 196,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: Colors.nuetral_grey_400,
  },
  input: {
    backgroundColor: Colors.nuetral_grey_100,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: Colors.nuetral_grey_400,
  },
  labelText: {
    fontSize: 16,
    paddingBottom: 4,
  },
  errorStar: {
    fontSize: 18,
    marginBottom: -2,
    color: "#d42121",
  },
  InputSearch: {
    fontSize: 14,
    color: "black",
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  inputError: {
    borderColor: "#d42121",
  },
  inputActive: {
    borderColor: Colors.primary_500,
  },
  hit: {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10,
  },
  errorText: {
    color: "#d42121",
    fontSize: 12,
    marginBottom: 10,
    height: 14,
  },
  space: {
    height: 10,
    marginBottom: 14,
  },
  labelRow: {
    flexDirection: "row",
  },
});

export default InputSearch;
